import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { removedRedirectFor } from "@/data/removedPages";
import { ArrowLeft, MapPin, Phone, Users, Calendar, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import MobileBookNowFab from "@/components/MobileBookNowFab";
import VenueLongform from "@/components/VenueLongform";
import { getVenue, CITY_ROUTE_PATH } from "@/data/cityVenues";
import { CITY_SEO } from "@/data/citySeo";

const CATEGORY_LABEL: Record<"events" | "roadtrips" | "business", string> = {
  events: "Events & Sports",
  roadtrips: "Regional Road Trip",
  business: "Business & Weddings",
};

export const Route = createFileRoute("/venues/$city/$venue")({
  beforeLoad: ({ params }) => {
    const moved = removedRedirectFor(`/venues/${params.city}/${params.venue}`);
    if (moved) throw redirect({ href: moved, statusCode: 301 });
  },
  loader: ({ params }) => {
    const data = getVenue(params.city, params.venue);
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Venue not found" }, { name: "robots", content: "noindex" }] };
    }
    const { city, venue } = loaderData;
    const title = `Sprinter Van Rental for ${venue.name} — ${city.cityLabel} | Group Transport`;
    const desc = `Rent a 12 or 15-passenger Mercedes Sprinter van for ${venue.name} in ${venue.location}. Group transport, tailgates, weddings, and event shuttles in ${city.cityLabel}. Book online in minutes.`;
    const url = `https://sprinter-van-rentals.com/venues/${params.city}/${params.venue}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Sprinter Van Rental",
          areaServed: { "@type": "Place", name: `${venue.name}, ${venue.location}` },
          provider: { "@type": "Organization", name: "Sprinter Van Rentals", url: "https://sprinter-van-rentals.com" },
          name: `Sprinter Van Rental for ${venue.name}`,
          description: desc,
          url,
        }),
      }],
    };
  },
  component: VenuePage,
});

function VenuePage() {
  const { city, venue, category } = Route.useLoaderData();
  const { city: citySlug } = Route.useParams();
  const cityRoute = CITY_ROUTE_PATH[citySlug] ?? "/";
  const isPilot = citySlug === "phoenix" && venue.slug === "state-farm-stadium";
  const cityShortName = CITY_SEO[citySlug]?.shortName ?? city.cityLabel;

  return (
    <main className="min-h-screen bg-white text-[#0F172A]">
      <Navbar />
      <MobileBookNowFab />

      <section className="pt-24 pb-8 border-b border-[#0F172A]/10 bg-gradient-to-b from-[#F97316]/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <Link
            to={cityRoute}
            className="inline-flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wider text-[#F97316] hover:text-[#0F172A] transition mb-6"
          >
            <ArrowLeft size={16} /> Back to {cityShortName} Sprinter van rentals
          </Link>
          <p className="font-display font-bold text-xs uppercase tracking-[0.4em] text-[#F97316] mb-3">
            {CATEGORY_LABEL[category as "events" | "roadtrips" | "business"]} · {city.cityLabel}
          </p>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4">
            Sprinter Van Rental for {venue.name}
          </h1>
          <p className="inline-flex items-center gap-2 text-[#0F172A]/70 text-base md:text-lg">
            <MapPin size={18} className="text-[#F97316]" /> {venue.location}
            {venue.distance ? <span className="ml-3 text-sm">· {venue.distance}</span> : null}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/book"
              search={{ step: 1 }}
              className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA6A0F] text-white font-display font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-full transition"
            >
              <Calendar size={16} /> Book Van Online
            </Link>
            <a
              href="tel:+19713282665"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0F172A] hover:bg-[#0F172A] hover:text-white text-[#0F172A] font-display font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-full transition"
            >
              <Phone size={16} /> Call to Book
            </a>
            <Link
              to={cityRoute}
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0F172A]/15 hover:border-[#0F172A] text-[#0F172A] font-display font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-full transition"
            >
              <ArrowLeft size={16} /> Back to {cityShortName} Sprinter van rentals
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-10">
          <div>
            <h2 className="font-display font-black text-2xl md:text-3xl mb-4">About {venue.name}</h2>
            <p className="text-[#0F172A]/85 text-lg leading-relaxed">{venue.blurb}</p>
          </div>

          <div>
            <h2 className="font-display font-black text-2xl md:text-3xl mb-4">
              Why groups rent a Sprinter for {venue.name}
            </h2>
            <p className="text-[#0F172A]/85 text-lg leading-relaxed mb-4">{venue.groupUse}</p>
            <p className="text-[#0F172A]/85 text-lg leading-relaxed">
              Our 12 and 15-passenger Mercedes Sprinter vans keep your group together on the way to
              {" "}{venue.name} — no split rideshares, no separated luggage, and no post-event surge
              pricing back to your {city.cityLabel} hotel.
            </p>
          </div>

          <div>
            <h2 className="font-display font-black text-2xl md:text-3xl mb-4">
              Common group trips to {venue.name}
            </h2>
            <ul className="space-y-3">
              {[
                `Airport pickup from the nearest ${city.cityLabel} airport and direct shuttle to ${venue.name}`,
                `Hotel-to-venue transport for wedding parties, bachelorette groups, and corporate teams`,
                `Tailgate-friendly parking with room for coolers, chairs, gear, and merch`,
                `Late-night return trip to your hotel — one driver, no rideshare surge`,
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-[#0F172A]/85 text-base leading-relaxed">
                  <Check size={20} className="text-[#F97316] shrink-0 mt-0.5" /> {line}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display font-black text-2xl md:text-3xl mb-4">
              Pickup & delivery in {city.cityLabel}
            </h2>
            <p className="text-[#0F172A]/85 text-lg leading-relaxed">
              We offer multiple pickup locations across the {city.cityLabel} metro. Once your
              vehicle is assigned, we'll provide the exact pickup address — typically within 24 hours
              of booking. Delivery is available in most cases; if the delivery address is more than
              25 miles from our pickup location there may be additional charges.
            </p>
          </div>
        </div>

        <aside className="md:sticky md:top-24 self-start rounded-2xl border border-[#0F172A]/10 bg-white shadow-sm p-6 space-y-5">
          <div>
            <p className="font-display font-bold text-xs uppercase tracking-[0.3em] text-[#F97316]">Ready to book?</p>
            <p className="font-display font-black text-2xl mt-1 leading-tight">Reserve your Sprinter for {venue.name}.</p>
          </div>
          <ul className="space-y-2 text-sm text-[#0F172A]/80">
            <li className="flex items-center gap-2"><Users size={16} className="text-[#F97316]" /> 12 or 15 passengers</li>
            <li className="flex items-center gap-2"><MapPin size={16} className="text-[#F97316]" /> Pickup in {city.cityLabel}</li>
            <li className="flex items-center gap-2"><Calendar size={16} className="text-[#F97316]" /> Instant online booking</li>
          </ul>
          <Link
            to="/book"
            search={{ step: 1 }}
            className="block text-center bg-[#F97316] hover:bg-[#EA6A0F] text-white font-display font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-full transition"
          >
            Book Van Online
          </Link>
          <Link
            to={cityRoute}
            className="block text-center bg-white border-2 border-[#0F172A]/15 hover:border-[#0F172A] text-[#0F172A] font-display font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-full transition"
          >
            Back to {city.cityLabel}
          </Link>
        </aside>
      </section>

      <VenueLongform
        slug={`${citySlug}-${venue.slug}`}
        venue={venue.name}
        location={venue.location}
        city={city.cityLabel}
      />

      <section className="border-t border-[#0F172A]/10 bg-[#0F172A]/5 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="font-display font-bold text-xs uppercase tracking-[0.3em] text-[#F97316] mb-2">Keep exploring</p>
            <p className="font-display font-black text-2xl">More {city.cityLabel} group trips.</p>
          </div>
          <Link
            to={cityRoute}
            className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#0F172A]/90 text-white font-display font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-full transition"
          >
            <ArrowLeft size={16} /> Back to {cityShortName} Sprinter van rentals
          </Link>
        </div>
      </section>
    </main>
  );
}
